import React from 'react';
import styled, { css } from 'styled-components';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { A, Link, H1, H2, H3 } from 'components/Typography';
import { th } from 'style/theme';
import { sizes } from 'style';
import TermDefinition from './components/TermDefinition';

const StyledImage = styled.img`
  width: 100%;
  :not(:first-child) {
    margin-top: 28px;
  }
`;

const Paragraph = styled.div`
  margin: 0;
  white-space: pre-line;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const ExternalLink = styled(A)`
  word-break: break-word;
`;

const InternalLink = styled(Link)`
  word-break: break-word;
`;

const Warning = styled.span`
  display: block;
  padding: 10px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.colors.lightYellow};
  color: ${({ theme }) => theme.colors.red};
  font-weight: bold;

  > * {
    color: ${({ theme }) => theme.colors.red};
    margin: 0;
    &:not(:last-child) {
      margin-bottom: 20px;
    }
  }
`;

const SmallText = styled.span`
  font-size: 13px;
  white-space: pre-line;
`;

const Highlight = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: #3babac;

  > ul,
  > ol {
    margin-top: 0;

    > li::before {
      background-color: #3babac !important;
    }
  }
`;

const Lowlight = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: #64748b;

  > ul,
  > ol {
    margin-top: 4px;
    margin-bottom: 24px;
  }
`;

const List = styled.ul`
  padding-left: 10px !important;

  @media ${sizes.tabletLandscape} {
    column-count: 2;
    column-gap: 40px;
  }
`;

const ListItem = styled.li`
  position: relative;
  margin-left: 32px;

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      color: #64748b;
    `}

  ::after {
    content: ' ';
    position: absolute;
    top: 0;
    left: -32px;
    width: 3px;
    height: 100%;

    ${({ variant }) =>
      variant === 'primary' &&
      css`
        background-color: ${th('colors.turquoise')};
      `}

    ${({ variant }) =>
      variant === 'secondary' &&
      css`
        background-color: #64748b;
      `}
  }
`;

export const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <Paragraph>{children}</Paragraph>,
    [BLOCKS.HEADING_1]: (node, children) => <H1>{children}</H1>,
    [BLOCKS.HEADING_2]: (node, children) => <H2>{children}</H2>,
    [BLOCKS.HEADING_3]: (node, children) => <H3>{children}</H3>,
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      if (node.data.target?.sys.contentType?.sys.id === 'policyHighlight') {
        return (
          <Highlight>
            <RichTextRenderer richText={node.data.target.fields.content} />
          </Highlight>
        );
      }
      if (node.data.target?.sys.contentType?.sys.id === 'policyLowlight') {
        return (
          <Lowlight>
            <RichTextRenderer richText={node.data.target.fields.content} />
          </Lowlight>
        );
      }
      if (node.data.target?.sys.contentType?.sys.id === 'policyList') {
        return (
          <List>
            {node.data.target.fields.list.map((listItem, i) => (
              <ListItem key={i} variant={node.data.target.fields.variant}>
                {listItem}
              </ListItem>
            ))}
          </List>
        );
      }
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      if (node.data.target.fields)
        return <StyledImage src={node.data.target.fields.file['en-US'].url} />;
      return null;
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      if (node.data.target?.sys.contentType?.sys.id === 'faqWarning') {
        return (
          <Warning>
            <RichTextRenderer richText={node.data.target.fields.warningContent} />
          </Warning>
        );
      }
      if (node.data.target?.sys.contentType?.sys.id === 'smallText') {
        return <SmallText>{node.data.target.fields.content}</SmallText>;
      }
      if (node.data.target?.sys.contentType?.sys.id === 'policyTermDefinition') {
        return (
          <TermDefinition term={node.data.target.fields.term}>
            <RichTextRenderer richText={node.data.target.fields.definition} />
          </TermDefinition>
        );
      }
      return null;
    },
    [INLINES.ASSET_HYPERLINK]: (node, children) => {
      return (
        <ExternalLink
          href={
            node.data && node.data.target.fields.file.url
              ? node.data.target.fields.file.url
              : null
          }
        >
          {children}
        </ExternalLink>
      );
    },
    [INLINES.HYPERLINK]: (node) => {
      if (node.data.uri.startsWith('customerServiceChat')) {
        return (
          <>
            {/* eslint-disable-next-line */}
            <InternalLink
              to="#"
              onClick={(e) => {
                e.preventDefault();
                window.Intercom('showNewMessage');
              }}
            >
              {node.content[0].value}
            </InternalLink>
          </>
        );
      }
      if (node.data.uri.startsWith('http') || node.data.uri.startsWith('mailto')) {
        return <ExternalLink href={node.data.uri}>{node.content[0].value}</ExternalLink>;
      }
      return <InternalLink to={node.data.uri}>{node.content[0].value}</InternalLink>;
    }
  }
};

const RichTextRenderer = ({ richText }) =>
  documentToReactComponents(richText, richTextOptions);

export default RichTextRenderer;
